import React, { useEffect, useState, useContext } from 'react';
import CardMarketRent from './CardMarketRent/CardMarketRent';
import CardLeaseAgreements from './CardLeaseAgreements/CardLeaseAgreements';
import CardTotalUnits from './CardTotalUnits/CardTotalUnits';
import { TaskContext } from '../DscrQuestionsAgreements';
import { Task, TaskResponse, TaskState } from 'gql/graphql';
import { useCreateResponseTask } from 'services/task/mutations';
import { useGetTaskResponsesByTask } from 'services/task/querys';
import SectionErrorTask from 'scenes/Private/components/SectionErrorTask/SectionErrorTask';
import CollapseIssues from 'scenes/Private/components/CollapseIssues/CollapseIssues';
import { formatterCurrency } from '@common/utils/formatters/formatter';
import SectionSuccessTask from 'scenes/Private/components/SectionSuccessTask/SectionSuccessTask';

interface Step2Props {
  task: Task | any;
  closeTask: Function;
}

const Step2 = ({ task, closeTask }: Step2Props) => {
  // @ts-ignore
  const { totalUnits, setTotalUnits, vacantUnits, setVacantUnits, occupiedUnits, setOccupiedUnits } = useContext(TaskContext);
  const [totalUnitsStep3, setTotalUnitsStep3] = useState<TaskResponse | any>();
  const [vacantUnitsStep3, setVacantUnitsStep3] = useState<TaskResponse | any>();
  const [responseLeaseAgreements, setResponseLeaseAgreements] = useState<TaskResponse | any>();
  const [taskCompleted, setTaskCompleted] = useState<boolean>();
  const { data: taskResponses, reexecute: getTaskResponses } = useGetTaskResponsesByTask({
    variables: { findResponsesByTask: { taskId: task._id } },
    pause: true
  });
  const handleTotalUnitsChange = (newTotalUnits: number, newVacantUnits: number) => {
    setTotalUnits(newTotalUnits);
    setVacantUnits(newVacantUnits);
  };

  const { execute: createResponseTask } = useCreateResponseTask();

  useEffect(() => {
    getTaskResponses({ requestPolicy: 'network-only' });
  }, [])

  useEffect(() => {
    if(taskResponses) {
      const step3Response_totalUnits = taskResponses.find((item) => item.key === 'units-in-property-dscr-step3') as TaskResponse;
      const step3Response_vacantUnits = taskResponses.find((item) => item.key === 'market-rent') as TaskResponse;
      const step3Response_leaseAgreements = taskResponses.find((item) => item.key === 'lease-agreements') as TaskResponse;
      setTotalUnitsStep3(step3Response_totalUnits);
      setVacantUnitsStep3(step3Response_vacantUnits);
      setResponseLeaseAgreements(step3Response_leaseAgreements);
      if (step3Response_totalUnits && !step3Response_totalUnits && !step3Response_vacantUnits && !step3Response_leaseAgreements) {
        setTotalUnits(totalUnitsStep3.metadata.totalUnits);
        // setVacantUnits(lastStep2Response.metadata.vacantUnits);
      } else if (step3Response_totalUnits && step3Response_vacantUnits) {
        setTotalUnits(step3Response_totalUnits.metadata.totalUnits);
        setVacantUnits(step3Response_vacantUnits.metadata.vacantUnits);
      }
      setTaskCompleted(step3Response_vacantUnits?.state === TaskState.Approved ||
        step3Response_leaseAgreements?.state === TaskState.Approved
      );
    }
  }, [taskResponses]);

  const saveResponse = async (key: string, label: string, metadata: any, state: TaskState, files?: any) => {
    const filteredFiles = files && files?.filter((item: any) => !!item.size);
    let payload: any = {
      key: key,
      label: label,
      step: 2,
      metadata: metadata,
      state: state,
      task: task._id,
      files: filteredFiles
    }
    if (totalUnitsStep3 && key === 'units-in-property-dscr-step3') payload._id = totalUnitsStep3._id;
    if (vacantUnitsStep3 && key === 'market-rent') payload._id = vacantUnitsStep3._id;
    if (responseLeaseAgreements && key === 'lease-agreements') payload._id = responseLeaseAgreements._id;
    await createResponseTask(payload);
    
  }

  const disabledEditQuantities = vacantUnitsStep3 || responseLeaseAgreements;

  return (
    <section className="Step3">
      <h2 className="h2">
        We need some more information about the units in this property
      </h2>
      <p className="h4 h4-w4">
        We’ll review the leases and calculate your final DSCR for you
      </p>
      {taskCompleted &&
          <SectionSuccessTask
            title="You've completed this task."
            button={{ title: "Back to dashboard", onClick: closeTask }}
          />
        }
      {!taskCompleted && 
        <SectionErrorTask
          taskId={task._id}
          componentDataError={(textDataError: any)=> {
            const currentData = vacantUnitsStep3?.metadata || textDataError;
            return (
            <div className='text-data'>
              <div className='row'>
                <span>Vacant units</span>
                <b>{currentData.vacantUnits}</b>
              </div>
              { currentData.rentsAndUnitNumbers.length > 0 &&
                currentData.rentsAndUnitNumbers.map((item: any, index: number) => (
                  <div className='row' key={index}>
                    <span>Unit number</span>
                    <b>{item.unitNumber}</b>
                    <span>Rent</span>
                    <b>{formatterCurrency(item.rent)}</b>
                  </div>
                ))
              }
            </div>
          )}}
        />
      }
      <div className="mt-25">
        <CardTotalUnits
          totalUnits={totalUnits}
          setTotalUnits={setTotalUnits}
          saveResponse={saveResponse}
          task={task}
        />
      </div>
      <div className="mt-25">
        <CardMarketRent 
          vacantUnits={vacantUnits} 
          setVacantUnits={setVacantUnits}
          totalUnits={totalUnits}
          setTotalUnits={setTotalUnits}
          occupiedUnits={occupiedUnits}
          saveResponse={saveResponse}
          task={task}
          disabledEditQuantities={disabledEditQuantities}
          />
      </div>
      <div className="mt-25">
        <CardLeaseAgreements
          occupiedUnits={occupiedUnits}
          setOccupiedUnits={setOccupiedUnits}
          totalUnits={totalUnits}
          setTotalUnits={setTotalUnits}
          ammountFiles={occupiedUnits}
          task={task}
          onTotalUnitsChange={handleTotalUnitsChange}
          disabledEditQuantities={disabledEditQuantities}
        />
      </div>

      <CollapseIssues
        taskId={task._id}
        taskKey={task.key}
        rejectionCode='no_action_required'
      />
    </section>
  );
};
export default Step2;
